'use strict';

const libPictView = require('pict-view');
const libFocus = require('../FocusFilters.js');

/**
 * IssueBoard -- the issues dashboard. A kanban over Issues (lanes by Status: Open -> Closed), the
 * standard project cuts (open count, assigned to me, recently closed), and a call-volume strip. One
 * read (IssueBoardAPI.dashboard -> AppData.IssueBoard) backs the whole page. HTML5 drag-and-drop
 * moves a card to another lane; the drop PUTs the issue's Status/SortOrder, which trips the
 * server-side status-change audit (WorkflowStatusEvent) and stamps ResolvedDate when it closes.
 * Clicking a card opens its detail.
 */
class PictViewIssueBoard extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._drag = null;
		this._filters = { Type: '', Customer: '', Assignee: '', Severity: '' };
	}

	_board() { return this.pict.providers.IssueBoardAPI; }
	_router() { return this.pict.providers['Pict-Router']; }

	// Load the dashboard, add display helpers, paint.
	show()
	{
		let tmpBoard = this._board();
		if (!tmpBoard) { return Promise.resolve(); }
		this._readUrl();
		return tmpBoard.dashboard().then(() => { this._rebuild(); this.render(); });
	}
	refresh() { return this.show(); }

	// Re-filter without reloading: just rebuild the view-lanes from the data already in AppData.
	setFilter(pKey, pValue)
	{
		if (Object.prototype.hasOwnProperty.call(this._filters, pKey)) { this._filters[pKey] = pValue || ''; }
		this._writeUrl();
		this._rebuild();
		this.render();
	}

	clearFilters()
	{
		this._filters = { Type: '', Customer: '', Assignee: '', Severity: '' };
		this._writeUrl();
		this._rebuild();
		this.render();
	}

	/**
	 * Put the narrowed board in the URL, so it can be sent to someone and survives a reload.
	 *
	 * PictView-Board has done this for its own filters all along; this board did not, so the same
	 * interaction had two different answers in one product -- a filtered kanban could be shared and a
	 * filtered issues board could not. replaceState rather than a route change, exactly as Board does
	 * it: the view keeps ownership of rendering and the router is never re-entered.
	 */
	_writeUrl() { libFocus.writeHashQuery(this._filters); }

	// Take the filters back off the URL on the way in, so a shared link lands on the same board.
	_readUrl()
	{
		let tmpParams = libFocus.readHashParams();
		Object.keys(this._filters).forEach((pKey) =>
		{
			if (typeof tmpParams[pKey] === 'string') { this._filters[pKey] = tmpParams[pKey]; }
		});
	}

	// Decorate the raw cards (colored border by type, a from-call marker), build the filter dropdowns
	// from the distinct values present, and apply the active filters to produce the view's lanes. The
	// provider owns the raw AppData.IssueBoard.Lanes; the view derives ViewLanes + Filters from them.
	_rebuild()
	{
		let tmpStore = (this.pict.AppData && this.pict.AppData.IssueBoard) || null;
		if (!tmpStore) { return; }
		let fDecorate = (pCard) =>
		{
			pCard.BorderStyle = pCard.TypeColor ? ('border-left-color:' + pCard.TypeColor) : '';
			pCard.FromCallMark = pCard.FromCall ? 'from a call' : '';
			pCard.NumberLabel = pCard.IssueNumber ? ('#' + pCard.IssueNumber) : '';
			return pCard;
		};
		(tmpStore.Lanes || []).forEach((pLane) => (pLane.Cards || []).forEach(fDecorate));
		(tmpStore.Mine || []).forEach(fDecorate);
		(tmpStore.RecentlyClosed || []).forEach(fDecorate);
		tmpStore.MineEmpty = (tmpStore.Mine && tmpStore.Mine.length) ? [] : [{ Message: 'Nothing assigned to you.' }];
		tmpStore.ClosedEmpty = (tmpStore.RecentlyClosed && tmpStore.RecentlyClosed.length) ? [] : [{ Message: 'No issues closed recently.' }];

		// Distinct values across every card, for the filter dropdowns.
		let tmpAll = [];
		(tmpStore.Lanes || []).forEach((pLane) => { tmpAll = tmpAll.concat(pLane.Cards || []); });
		let tmpFilters = this._filters;
		let fDistinct = (pField) => { let tmpSeen = {}; tmpAll.forEach((pC) => { if (pC[pField]) { tmpSeen[pC[pField]] = true; } }); return Object.keys(tmpSeen).sort(); };
		let fOptions = (pValues, pCurrent, pAllLabel) => [{ Value: '', Label: pAllLabel, Sel: pCurrent ? '' : 'selected' }]
			.concat(pValues.map((pV) => ({ Value: pV, Label: pV, Sel: (String(pV) === String(pCurrent)) ? 'selected' : '' })));
		tmpStore.Filters =
		{
			Type: fOptions(fDistinct('TypeName'), tmpFilters.Type, 'All types'),
			Customer: fOptions(fDistinct('CustomerName'), tmpFilters.Customer, 'All customers'),
			Assignee: fOptions(fDistinct('AssigneeName'), tmpFilters.Assignee, 'All assignees'),
			Severity: fOptions(fDistinct('Severity'), tmpFilters.Severity, 'All severities'),
			ClearSlot: (tmpFilters.Type || tmpFilters.Customer || tmpFilters.Assignee || tmpFilters.Severity) ? [{}] : []
		};

		// Apply the filters to each lane to produce the rendered lanes.
		let fMatch = (pCard) => (!tmpFilters.Type || pCard.TypeName === tmpFilters.Type)
			&& (!tmpFilters.Customer || pCard.CustomerName === tmpFilters.Customer)
			&& (!tmpFilters.Assignee || pCard.AssigneeName === tmpFilters.Assignee)
			&& (!tmpFilters.Severity || pCard.Severity === tmpFilters.Severity);
		tmpStore.ViewLanes = (tmpStore.Lanes || []).map((pLane) =>
		{
			let tmpCards = (pLane.Cards || []).filter(fMatch);
			return { Key: pLane.Key, Label: pLane.Label, Cards: tmpCards, Count: tmpCards.length };
		});
	}

	// Surface a message to the user. The board already toasts on a failed drop; new-issue failures used to
	// be swallowed, so the button looked dead when the create was refused or errored. Always say what
	// happened instead of doing nothing.
	_toast(pMsg, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMsg, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	newIssue()
	{
		let tmpAPI = this.pict.providers.IssueAPI;
		let tmpRouter = this._router();
		if (!tmpAPI)
		{
			this._toast('Could not add an issue: the Issue service is not available. Reload the page and try again.', 'error');
			return;
		}
		// EntityAPI.create rejects on any non-2xx (throws 'HTTP <status>'); catch it and tell the user
		// rather than letting the button silently do nothing.
		tmpAPI.create({ Title: 'New issue', Status: 'Open', Priority: 'Normal' }).then((pRow) =>
		{
			if (pRow && pRow.IDIssue && tmpRouter) { tmpRouter.navigate('/issue/' + pRow.IDIssue); }
			else if (pRow && pRow.IDIssue) { this.show(); }
			else { this._toast('Could not add an issue: the server did not return a new record.', 'error'); }
		}).catch((pErr) =>
		{
			this._toast('Could not add an issue: ' + ((pErr && pErr.message) || pErr), 'error');
		});
	}

	openIssue(pID)
	{
		let tmpRouter = this._router();
		if (tmpRouter && pID) { tmpRouter.navigate('/issue/' + pID); }
	}

	dragStart(pEvent, pID)
	{
		this._drag = { ID: pID };
		if (pEvent && pEvent.dataTransfer)
		{
			pEvent.dataTransfer.effectAllowed = 'move';
			try { pEvent.dataTransfer.setData('text/plain', String(pID)); } catch (pErr) { /* ignore */ }
		}
	}

	allowDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		if (pEvent && pEvent.dataTransfer) { pEvent.dataTransfer.dropEffect = 'move'; }
	}

	drop(pEvent, pLaneKey)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		let tmpDrag = this._drag;
		this._drag = null;
		let tmpBoard = this._board();
		if (!tmpDrag || !tmpBoard) { return; }
		let tmpSort = tmpBoard.nextSortOrder(pLaneKey);
		tmpBoard.moveCard(tmpDrag.ID, pLaneKey, tmpSort)
			.then(() => this.show())
			.catch((pErr) => { this._toast('Move failed: ' + ((pErr && pErr.message) || pErr), 'error'); });
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewIssueBoard;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-IssueBoard',
	DefaultRenderable: 'IssueBoard-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-issues { height: 100%; display: flex; flex-direction: column; gap: 14px; }
		.ps-issues-head { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
		.ps-issues-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-issues-new { font: inherit; font-size: 13px; border: 1px solid var(--theme-color-brand-primary, #2880a6); background: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-text-on-brand, #fff); border-radius: 7px; padding: 6px 13px; cursor: pointer; }
		.ps-issues-new:hover { filter: brightness(1.06); }
		.ps-issues-stats { display: flex; gap: 8px; flex-wrap: wrap; margin-left: auto; }
		.ps-stat { background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 4px 11px; font-size: 12px; }
		.ps-stat b { color: var(--theme-color-text-primary, #2c3140); font-weight: 700; }
		.ps-stat-calls { background: var(--theme-color-status-info, #e7f1fb); color: var(--theme-color-text-secondary, #41506a); }
		.ps-issues-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
		.ps-issues-filters select { font: inherit; font-size: 12px; padding: 4px 8px; border-radius: 6px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-issues-filter-clear { font: inherit; font-size: 12px; border: none; background: none; color: var(--theme-color-brand-primary, #2880a6); cursor: pointer; padding: 4px 6px; }
		.ps-issues-filter-clear:hover { text-decoration: underline; }
		/* One scroll region for the whole board (both axes); lanes and card stacks never scroll on
		   their own, so a busy lane can't spawn nested column scrollbars. */
		.ps-issues-lanes { display: flex; gap: 12px; overflow: auto; align-items: flex-start; flex: 1; min-height: 0; padding-bottom: 8px; }
		.ps-ilane { flex: 0 0 240px; width: 240px; background: var(--theme-color-background-secondary, #f1f3f7); border-radius: 10px; padding: 10px; display: flex; flex-direction: column; }
		.ps-ilane-head { display: flex; align-items: center; justify-content: space-between; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); padding: 4px 6px 10px; }
		.ps-ilane-count { background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 1px 8px; font-size: 12px; }
		.ps-ilane-cards { display: flex; flex-direction: column; gap: 8px; min-height: 40px; }
		.ps-icard { background: var(--theme-color-background-panel, #fff); border-radius: 8px; padding: 9px 11px; border-left: 3px solid var(--theme-color-border-default, #d8dde6); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
		.ps-icard:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
		.ps-icard[draggable="true"]:active { cursor: grabbing; }
		.ps-icard-top { display: flex; align-items: baseline; gap: 6px; margin-bottom: 5px; }
		.ps-icard-num { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); font-weight: 700; }
		.ps-icard-title { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); line-height: 1.25; }
		.ps-icard-meta { display: flex; gap: 5px; flex-wrap: wrap; }
		.ps-chip { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-chip:empty { display: none; }
		.ps-chip-type { background: var(--theme-color-background-tertiary, #e3e8f0); }
		.ps-chip-call { background: var(--theme-color-status-info, #e7f1fb); color: var(--theme-color-text-secondary, #41506a); }
		.ps-chip-cust { background: transparent; color: var(--theme-color-text-muted, #97a1ab); padding-left: 0; }
		.ps-issues-cuts { display: flex; gap: 16px; flex: 0 0 auto; }
		.ps-cut { flex: 1 1 0; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 10px; padding: 12px 14px; max-height: 190px; overflow-y: auto; }
		.ps-cut h3 { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 8px; }
		.ps-cut-row { display: flex; align-items: baseline; gap: 7px; padding: 4px 0; cursor: pointer; border-bottom: 1px solid var(--theme-color-border-light, #f0f3f6); }
		.ps-cut-row:hover { color: var(--theme-color-brand-primary, #2880a6); }
		.ps-cut-row .ps-icard-num { flex: 0 0 auto; }
		.ps-cut-row .t { font-size: 13px; color: inherit; }
		.ps-cut-empty { color: var(--theme-color-text-muted, #97a1ab); font-size: 13px; padding: 6px 0; }
	`,
	Templates:
	[
		{
			Hash: 'IssueBoard-Display',
			Template: /*html*/`
<div class="ps-page ps-issues">
	<div class="ps-issues-head">
		<h2>Issues</h2>
		<button class="ps-issues-new" onclick="_Pict.views['Plansheet-IssueBoard'].newIssue()">+ New issue</button>
		<div class="ps-issues-stats">
			<span class="ps-stat"><b>{~D:AppData.IssueBoard.Counts.Open~}</b> open</span>
			<span class="ps-stat"><b>{~D:AppData.IssueBoard.Counts.Mine~}</b> mine</span>
			<span class="ps-stat"><b>{~D:AppData.IssueBoard.Counts.ClosedRecently~}</b> recently closed</span>
			<span class="ps-stat ps-stat-calls"><b>{~D:AppData.IssueBoard.CallVolume.Last7~}</b> calls / 7d, <b>{~D:AppData.IssueBoard.CallVolume.Last30~}</b> / 30d</span>
		</div>
	</div>
	<div class="ps-issues-filters">
		<select onchange="_Pict.views['Plansheet-IssueBoard'].setFilter('Type', this.value)">{~TS:IssueBoard-FilterOpt:AppData.IssueBoard.Filters.Type~}</select>
		<select onchange="_Pict.views['Plansheet-IssueBoard'].setFilter('Customer', this.value)">{~TS:IssueBoard-FilterOpt:AppData.IssueBoard.Filters.Customer~}</select>
		<select onchange="_Pict.views['Plansheet-IssueBoard'].setFilter('Assignee', this.value)">{~TS:IssueBoard-FilterOpt:AppData.IssueBoard.Filters.Assignee~}</select>
		<select onchange="_Pict.views['Plansheet-IssueBoard'].setFilter('Severity', this.value)">{~TS:IssueBoard-FilterOpt:AppData.IssueBoard.Filters.Severity~}</select>
		{~TS:IssueBoard-FilterClear:AppData.IssueBoard.Filters.ClearSlot~}
	</div>
	<div class="ps-issues-lanes">{~TS:IssueBoard-Lane:AppData.IssueBoard.ViewLanes~}</div>
	<div class="ps-issues-cuts">
		<div class="ps-cut">
			<h3>Assigned to me</h3>
			{~TS:IssueBoard-CutRow:AppData.IssueBoard.Mine~}
			{~TS:IssueBoard-CutEmpty:AppData.IssueBoard.MineEmpty~}
		</div>
		<div class="ps-cut">
			<h3>Recently closed</h3>
			{~TS:IssueBoard-CutRow:AppData.IssueBoard.RecentlyClosed~}
			{~TS:IssueBoard-CutEmpty:AppData.IssueBoard.ClosedEmpty~}
		</div>
	</div>
</div>`
		},
		{
			Hash: 'IssueBoard-Lane',
			Template: /*html*/`
<div class="ps-ilane" ondragover="_Pict.views['Plansheet-IssueBoard'].allowDrop(event)" ondrop="_Pict.views['Plansheet-IssueBoard'].drop(event,'{~D:Record.Key~}')">
	<div class="ps-ilane-head"><span>{~D:Record.Label~}</span><span class="ps-ilane-count">{~D:Record.Count~}</span></div>
	<div class="ps-ilane-cards">{~TS:IssueBoard-Card:Record.Cards~}</div>
</div>`
		},
		{
			Hash: 'IssueBoard-Card',
			Template: /*html*/`
<div class="ps-icard" style="{~D:Record.BorderStyle~}" draggable="true" ondragstart="_Pict.views['Plansheet-IssueBoard'].dragStart(event,{~D:Record.IDIssue~})" onclick="_Pict.views['Plansheet-IssueBoard'].openIssue({~D:Record.IDIssue~})">
	<div class="ps-icard-top"><span class="ps-icard-num">{~D:Record.NumberLabel~}</span><span class="ps-icard-title">{~D:Record.Title~}</span></div>
	<div class="ps-icard-meta"><span class="ps-chip ps-chip-type">{~D:Record.TypeName~}</span><span class="ps-chip">{~D:Record.Priority~}</span><span class="ps-chip ps-chip-cust">{~D:Record.CustomerName~}</span><span class="ps-chip ps-chip-call">{~D:Record.FromCallMark~}</span></div>
</div>`
		},
		{
			Hash: 'IssueBoard-CutRow',
			Template: /*html*/`<div class="ps-cut-row" onclick="_Pict.views['Plansheet-IssueBoard'].openIssue({~D:Record.IDIssue~})"><span class="ps-icard-num">{~D:Record.NumberLabel~}</span><span class="t">{~D:Record.Title~}</span></div>`
		},
		{
			Hash: 'IssueBoard-CutEmpty',
			Template: /*html*/`<div class="ps-cut-empty">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'IssueBoard-FilterOpt',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.Sel~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'IssueBoard-FilterClear',
			Template: /*html*/`<button class="ps-issues-filter-clear" onclick="_Pict.views['Plansheet-IssueBoard'].clearFilters()">Clear filters</button>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'IssueBoard-Display',
			TemplateHash: 'IssueBoard-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
